import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProgressBar from '../components/ProgressBar';
import ScrollToTop from '../components/ScrollToTop';

const PrivacyPolicy = () => {
  const { isDark } = useDarkMode();

  const sections = [
    {
      title: '1. Introduction',
      content: 'Media Society ("we", "us", "our") respects the privacy of our users and clients. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.'
    },
    {
      title: '2. Information We Collect',
      content: 'We collect information you voluntarily provide, including but not limited to: your name, email address, phone number, company information, and any communication content. We also automatically collect certain information about your device and browsing activity, such as IP address, browser type, pages visited, and referring URLs.'
    },
    {
      title: '3. How We Use Your Information',
      content: 'Your information is used to: respond to your inquiries, provide our services, send marketing communications (with your consent), improve our website and services, and comply with legal obligations. We may also use analytics to understand user behavior and enhance our website experience.'
    },
    {
      title: '4. Data Security',
      content: 'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure. We encourage you to use a strong password and keep your account credentials confidential.'
    },
    {
      title: '5. Third-Party Services',
      content: 'We may use third-party service providers to assist with website functionality, analytics, payment processing, and marketing. These providers are bound by confidentiality agreements and authorized to use your information only as necessary to provide these services.'
    },
    {
      title: '6. Cookies',
      content: 'We use cookies and similar tracking technologies to enhance your user experience. You can control cookie preferences through your browser settings. For detailed information about cookies, please refer to our Cookies Policy.'
    },
    {
      title: '7. Your Rights',
      content: 'You have the right to access, correct, or delete your personal data. You also have the right to object to processing and to data portability. To exercise these rights, please contact us at ahtashamspeaks@gmail.com.'
    },
    {
      title: '8. Children\'s Privacy',
      content: 'Our services are not directed to children under 18 years old. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information and terminate the child\'s involvement.'
    },
    {
      title: '9. Changes to This Policy',
      content: 'We may update this Privacy Policy from time to time. We will notify you of changes by posting the new policy on this page and updating the "Effective Date" at the top of the policy. Your continued use of our services after changes constitutes your acceptance of the new policy.'
    },
    {
      title: '10. Contact Us',
      content: 'If you have questions about this Privacy Policy or our privacy practices, please contact us at: Email: ahtashamspeaks@gmail.com | Phone: 03047259307 | Address: Lahore, Pakistan'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className={`w-full transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-light-secondary'}`}>
      <ProgressBar />
      <ScrollToTop />
      <Navbar />

      <section className={`w-full py-20 lg:py-32 transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-light-secondary'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            className="mb-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
          >
            <motion.h1
              className={`font-montserrat font-bold text-4xl lg:text-5xl mb-4 transition-colors ${
                isDark ? 'text-white' : 'text-black'
              }`}
              variants={itemVariants}
            >
              Privacy Policy
            </motion.h1>
            <motion.p
              className={`font-poppins text-lg transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              variants={itemVariants}
            >
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </motion.p>
          </motion.div>

          {/* Content Sections */}
          <motion.div
            className="space-y-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: '-50px' }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`p-6 rounded-lg border transition-all duration-300 ${
                  isDark
                    ? 'bg-dark-secondary border-primary-gold/20 hover:border-primary-gold/40'
                    : 'bg-light-tertiary border-primary-gold/20 hover:border-primary-gold/40'
                }`}
              >
                <h2 className={`font-montserrat font-bold text-xl mb-3 transition-colors ${
                  isDark ? 'text-primary-gold' : 'text-primary-gold'
                }`}>
                  {section.title}
                </h2>
                <p className={`font-poppins leading-relaxed transition-colors ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {section.content}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
